if (!customElements.get("event-timer")) {
  class EventTimer extends HTMLElement {
    constructor() {
      super();
      this._tick = this._tick.bind(this);
    }

    parseTime(value) {
      const timeStr = (value || "").trim();
      if (timeStr) {
        const match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
        if (match) {
          const hh = Number(match[1]);
          const mm = Number(match[2]);
          if (Number.isFinite(hh) && Number.isFinite(mm) && hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) {
            return { hours: hh, minutes: mm };
          }
        }
      }

      return { hours: 0, minutes: 0 };
    }

    getStartTimeParts() {
      return this.parseTime(this.dataset.start);
    }

    getEndTimeParts() {
      if (this.dataset.time) {
        return this.parseTime(this.dataset.time);
      }

      const hours = Number(this.dataset.hours);
      const minutes = Number(this.dataset.minutes);
      return {
        hours: Number.isFinite(hours) && hours >= 0 && hours <= 23 ? hours : 0,
        minutes: Number.isFinite(minutes) && minutes >= 0 && minutes <= 59 ? minutes : 0,
      };
    }

    getOffsetMinutes() {
      const raw = (this.dataset.offset || "").trim();
      const match = raw.match(/^([+-])(\d{2}):?(\d{2})$/);
      if (!match) return null;

      const sign = match[1] === "-" ? -1 : 1;
      return sign * (Number(match[2]) * 60 + Number(match[3]));
    }

    getShopDateParts(nowTs) {
      const offsetMin = this.getOffsetMinutes();
      const date = offsetMin === null ? new Date(nowTs) : new Date(nowTs + offsetMin * 60000);

      if (offsetMin === null) {
        return {
          year: date.getFullYear(),
          month: date.getMonth(),
          date: date.getDate(),
        };
      }

      return {
        year: date.getUTCFullYear(),
        month: date.getUTCMonth(),
        date: date.getUTCDate(),
      };
    }

    getTodayTimestamp(nowTs, hours, minutes) {
      const offsetMin = this.getOffsetMinutes();
      const parts = this.getShopDateParts(nowTs);

      if (offsetMin === null) {
        return new Date(parts.year, parts.month, parts.date, hours, minutes, 0, 0).getTime();
      }

      return Date.UTC(parts.year, parts.month, parts.date, hours, minutes, 0, 0) - offsetMin * 60000;
    }

    isInWindow(nowTs) {
      const start = this.getStartTimeParts();
      const end = this.getEndTimeParts();
      const startMin = start.hours * 60 + start.minutes;
      const endMin = end.hours * 60 + end.minutes;
      const startTs = this.getTodayTimestamp(nowTs, start.hours, start.minutes);
      const endTs = this.getTodayTimestamp(nowTs, end.hours, end.minutes);

      if (startMin < endMin) {
        return nowTs >= startTs && nowTs < endTs;
      }

      if (startMin > endMin) {
        return nowTs >= startTs || nowTs < endTs;
      }

      if (endMin === 0) return true;
      return nowTs < endTs;
    }

    getEndTimestamp(nowTs) {
      const start = this.getStartTimeParts();
      const end = this.getEndTimeParts();
      const startMin = start.hours * 60 + start.minutes;
      const endMin = end.hours * 60 + end.minutes;
      const startTs = this.getTodayTimestamp(nowTs, start.hours, start.minutes);
      let endTs = this.getTodayTimestamp(nowTs, end.hours, end.minutes);

      if (startMin === 0 && endMin === 0) {
        return endTs + 86400000;
      }

      if (startMin > endMin && nowTs >= startTs) {
        endTs += 86400000;
      }

      return endTs;
    }

    isDesignMode() {
      return Boolean(window.Shopify && window.Shopify.designMode);
    }

    hideBanner() {
      const section = this.closest(".shopify-section");
      if (section) {
        section.hidden = true;
        return;
      }

      const banner = this.closest(".event-countdown");
      if (banner) banner.hidden = true;
    }

    connectedCallback() {
      this._hoursEl = this.querySelector(".hours .event-timer--column--number");
      this._minutesEl = this.querySelector(".minutes .event-timer--column--number");
      this._secondsEl = this.querySelector(".seconds .event-timer--column--number");

      const now = Date.now();
      if (!this.isInWindow(now) && !this.isDesignMode()) {
        this.hideBanner();
        return;
      }

      this._targetTs = this.getEndTimestamp(now);
      this._tick();
    }

    disconnectedCallback() {
      if (this._timerId) {
        clearTimeout(this._timerId);
        this._timerId = null;
      }
    }

    _tick() {
      const now = Date.now();
      let distance = this._targetTs - now;

      if (distance <= 0 || !this.isInWindow(now)) {
        if (!this.isDesignMode()) {
          this.hideBanner();
          return;
        }

        distance = Math.max(distance, 0);
      }

      const hours = Math.floor(distance / 3600000);
      const minutes = Math.floor((distance % 3600000) / 60000);
      const seconds = Math.floor((distance % 60000) / 1000);

      if (this._hoursEl) this._hoursEl.textContent = EventTimer.addZero(hours);
      if (this._minutesEl) this._minutesEl.textContent = EventTimer.addZero(minutes);
      if (this._secondsEl) this._secondsEl.textContent = EventTimer.addZero(seconds);

      if (distance <= 0) return;

      const nextDelay = 250 + (1000 - (now % 1000));
      this._timerId = setTimeout(this._tick, nextDelay);
    }

    static addZero(value) {
      return value < 10 && value >= 0 ? "0" + value : value;
    }
  }

  customElements.define("event-timer", EventTimer);
}
